<template>
  <div class="main-container">
    <div class="pickup-time-modal">
      <form action="" class="pickup-time-modal__form">
        <div class="m-t-70" />
        <label class="iw-visuallyhidden" for="pickup-time-modal-close-btn">close</label>
        <button id="pickup-time-modal-close-btn" class="pickup-time-modal__close-btn" @click="closeModal">
          <svg
            class="pickup-time-modal__image svg-inline--fa"
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
          >
            <path
              fill="currentColor"
              stroke="currentColor"
              stroke-linecap="square"
              stroke-width="2"
              d="M1.368 1.368l13.264 13.264m0-13.264L1.368 14.632"
            />
          </svg>
        </button>
        <div v-if="errorMessage" class="pickup-time-modal__notifier">
          <app-notifier :type="'failed'">
            <div>{{ errorMessage }}</div>
          </app-notifier>
        </div>
        <h3 class="pickup-time-modal__title iw-page-title">{{ title || "Schedule my order" }}</h3>
        <div class="iw-hyperlink pickup-time-modal__section">
          <app-radio-button
            group-name="pickUpTimeOptions"
            :model-value="String(now)"
            payload="true"
            :label="getPickupEstimatedTimeRange(vendorId)"
            :disabled="!isPickupTimeSelectionEnableByVendorId(vendorId)"
            @update:modelValue="toggleNow"
          />
          <app-radio-button
            group-name="pickUpTimeOptions"
            :model-value="String(now)"
            payload="false"
            label="Later"
            :disabled="!isPickupLaterAvailable(vendorId) || !isVendorOpenByVendorId(vendorId)"
            @update:modelValue="toggleNow"
          />
        </div>
        <div v-if="!now && isPickupLaterAvailable(vendorId)" class="pickup-time-modal__schedule">
          <app-select-box
            class="m-b-20 iw-body-text-small"
            :label-text="'Date'"
            :options="dateOptions"
            :selected="selectedDateId"
            :on-change-cb="handleDateChange"
          />
          <app-select-box
            class="iw-body-text-small"
            :label-text="'Time'"
            :options="timeOptions"
            :default-option-text="'Select Time'"
            :has-default-option-null="true"
            :selected="selectedTimeId"
            :on-change-cb="handleTimeChange"
          />
        </div>
        <div class="pickup-time-modal__actions">
          <app-button
            :text="'Close'"
            :aria-label="'Close'"
            class="pickup-time-modal__section__close"
            :handler="closeModal"
            :outlined="true"
          >
          </app-button>
          <app-button
            :text="buttonText || 'Continue'"
            :aria-label="buttonText || 'Continue'"
            class="pickup-time-modal__section__continue"
            :is-disabled="!canContinue"
            :handler="handleContinue"
          >
          </app-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { DateTime } from "luxon";
import { useOrderAheadMapper } from "@/store/modules/orderAhead/mapper";
import { DEFAULT_TIMEZONE, findClosestIndex } from "@/utils";

export const { mapOptionsByNamedDays, mapTimeOptionsByDate } = useOrderAheadMapper();

export default {
  name: "PickupTimeModal",
  props: {
    closeModal: Function,
    errorMessage: String | null,
    title: String,
    buttonText: String,
    onContinue: Function,
    selectedDate: String,
    selectedTime: String,
    setSelectedDate: Function,
    setSelectedTime: Function,
    toConfirmationScreen: Function,
    vendorId: Number
  },
  data() {
    return {
      now: true,
      timezone: DEFAULT_TIMEZONE,
      pickupTimes: [],
      dateOptions: [{ name: "Loading..." }],
      timeOptions: [],
      intialDate: ""
    };
  },
  mounted: function () {
    this.timezone = this.getVenueTimezone;
    this.pickupTimes = this.getValidPickupTimes(this.vendorId);
    this.dateOptions = mapOptionsByNamedDays(this.pickupTimes, this.timezone);
    this.intialDate = this.getInitialDate();
    this.setSelectedDate(this.intialDate);
    this.timeOptions = mapTimeOptionsByDate(this.intialDate, this.pickupTimes, this.timezone);
    this.now = this.selectedPickup === this.pickupTimes[0] && this.isPickupTimeSelectionEnableByVendorId(this.vendorId);

    if (!this.now) {
      if (!this.timeOptions.find((timeOption) => timeOption.id === this.selectedPickup)) {
        // Find the closest timeslot to the unavailable selected one
        const selectedPickupInMilliseconds = DateTime.fromISO(this.selectedPickup).setZone(this.timezone).toMillis();
        const pickupTimesInMilliseconds = this.pickupTimes.map((pickupTime) =>
          DateTime.fromISO(pickupTime).setZone(this.timezone).toMillis()
        );
        const closestIndex = findClosestIndex(pickupTimesInMilliseconds, selectedPickupInMilliseconds);

        if (closestIndex === 0) this.now = true;
        this.setSelectedTime(this.pickupTimes[closestIndex]);
      }
    }
  },
  computed: {
    canContinue() {
      return this.now || this.selectedTime;
    },
    selectedDateId() {
      return this.dateOptions.find((dateOption) => dateOption.id === this.selectedDate)?.id;
    },
    selectedTimeId() {
      const checkTime = this.selectedTime || this.selectedPickup;
      return this.timeOptions.find((timeOption) => timeOption.id === checkTime)?.id;
    },
    ...mapGetters({
      isVendorOpenByVendorId: "vendor/isVendorOpenByVendorId",
      isPickupTimeSelectionEnableByVendorId: "vendor/isPickupTimeSelectionEnableByVendorId",
      getValidPickupTimes: "vendor/getValidPickupTimesByVendorId",
      getPickupEstimatedTimeRange: "vendor/getPickupEstimatedTimeRange",
      getUnavailableCartItems: "cart/getUnavailableCartItems",
      getVenueTimezone: "venue/getVenueTimezone",
      selectedPickup: "orderAhead/selectedPickup"
    })
  },
  methods: {
    getInitialDate() {
      if (this.selectedTime) {
        return this.selectedDate;
      }
      if (this.selectedPickup) {
        return DateTime.fromISO(this.selectedPickup).setZone(this.timezone).toFormat("yyyy-MM-dd");
      }
      return this.dateOptions?.[0]?.id;
    },
    handleContinue() {
      if (this.getUnavailableCartItems(this.selectedTime)?.length) {
        return this.toConfirmationScreen();
      }
      if (this.now) {
        this.setSelectedTime(this.getValidPickupTimes(this.vendorId)[0]);
      }
      this.onContinue();
    },
    handleDateChange(event) {
      this.timeOptions = mapTimeOptionsByDate(event.target.value, this.pickupTimes, this.timezone);
      this.setSelectedDate(event.target.value);
      this.setSelectedTime(null);
    },
    handleTimeChange(event) {
      this.setSelectedTime(event.target.value);
    },
    toggleNow() {
      this.now = !this.now;
      if (this.now) {
        this.setSelectedDate(null);
        this.setSelectedTime(this.pickupTimes[0]);
      }
    },
    isPickupLaterAvailable(vendorId) {
      return !!this.getValidPickupTimes(vendorId)?.length;
    }
  }
};
</script>
<style lang="scss" scoped>
.dark {
  .terms-of-use-modal {
    color: $primary-color;
  }
  a:link {
    color: $group-1-color-2;
  }
  a:visited {
    color: $group-1-color-2;
  }
}

.main-container {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.pickup-time-modal {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  overflow: auto;
  width: 100%;
  height: 100%;
  font-family: $fontFamilyPrimary;
  color: $group-3-color-2;
  &__form {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 393px;
    margin-left: $spacer;
    margin-right: $spacer;
    @include respond-to-mobile {
      width: 100%;
    }
  }
  &__title {
    width: 100%;
    text-align: left;
    @include respond-to-mobile {
      margin-top: 104.7px;
    }
  }
  &__close-btn {
    all: unset;
    cursor: pointer;
    position: absolute;
    top: ($spacer * 2);
    right: ($spacer * 2);
    left: auto;
    svg {
      color: $group-3-color-2;
      width: 14px;
      height: 14px;
    }
    @include respond-to-mobile {
      cursor: pointer;
      position: absolute;
      left: $spacer;
    }
  }
  &__schedule {
    margin-top: 30px;
    width: 100%;
  }
  &__section {
    margin-top: 29px;
    display: flex;
    justify-content: flex-start;
    width: 100%;
    @include respond-to-mobile {
      margin-top: 39.3px;
    }
    &__continue,
    &__close {
      width: 45%;
    }
  }
  &__actions {
    display: flex;
    justify-content: space-around;
    margin-top: 48px;
    margin-bottom: 70px;
    width: 100%;
  }

  &__notifier {
    display: block;
    width: 100%;
  }
}
</style>
